import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, CalendarDays, IndianRupee, Users } from "lucide-react";
import poster from "../assets/young-founders/poster.jpg";
import { youngFounders, youngFoundersState } from "../data/youngFounders";
import "./YoungFoundersBanner.scss";

// ─── Young Founders band, directly under the CoGrad hero ─────────────────────
// Same shape as HackathonBanner, which this sits above while entries are open:
// identity and the action on the left, the creative on the right, every value
// read from src/data/youngFounders.js.
//
// It takes itself down. `youngFoundersState()` is the canonical helper — the
// one the home-page popup already uses — so on the 31st this band disappears
// and the YUVASETU band returns to sitting directly under the hero, with
// nobody editing a file. `?yf=open` forces it back for a check.
//
// The official banner is the only show creative here: landscape on desktop,
// the portrait cut on phones, and we never rebuild the show's own logos.

const EASE = [0.2, 0.7, 0.1, 1];

const rise = (delay, y = 22) => ({
  initial: { opacity: 0, y },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: EASE, delay },
});

const FACTS = [
  { Icon: Users, label: "Who", value: "Ages 6–15" },
  { Icon: IndianRupee, label: "Fee", value: "Free" },
  { Icon: CalendarDays, label: "Entries close", value: youngFounders.closesLabel },
];

export default function YoungFoundersBanner() {
  if (youngFoundersState() !== "open") return null;

  return (
    <section className="yfb" aria-labelledby="yfb-title">
      <div className="yfb__inner">
        {/* ── Left: what it is, the facts, the action ───────────────────── */}
        <div className="yfb__copy">
          <motion.p className="yfb__pill" {...rise(0)}>
            <span aria-hidden="true" />
            Registrations open
          </motion.p>

          <motion.h2 className="yfb__h2" id="yfb-title" {...rise(0.06)}>
            Shark Tank India Season 6
            <span>Young Founders Special</span>
          </motion.h2>

          <motion.p className="yfb__lead" {...rise(0.12)}>
            A round made for kids, not grown-ups. If your child is between 6 and 15
            and has a business idea, it can go on national television — no company,
            no revenue, and nothing to pay.
          </motion.p>

          <motion.ul className="yfb__facts" aria-label="Key details" {...rise(0.18)}>
            {FACTS.map((f) => (
              <li key={f.label}>
                <span className="yfb__fact-icon" aria-hidden="true">
                  <f.Icon size={17} strokeWidth={2} />
                </span>
                <small>{f.label}</small>
                {f.value}
              </li>
            ))}
          </motion.ul>

          <motion.div className="yfb__actions" {...rise(0.24, 26)}>
            <Link to={youngFounders.path} className="yfb__btn">
              See the details
              <ArrowUpRight size={17} strokeWidth={2.4} aria-hidden="true" />
            </Link>
            <a
              className="yfb__link"
              href={youngFounders.registerUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Apply on the official site
            </a>
          </motion.div>

          <motion.p className="yfb__note" {...rise(0.3)}>
            CoGrad is an outreach partner. Registration, selection and all
            communication are handled by Sony Pictures Networks India.
          </motion.p>
        </div>

        {/* ── Right: the official creative ──────────────────────────────── */}
        <motion.div className="yfb__art" {...rise(0.14, 26)}>
          <Link to={youngFounders.path} aria-label="Shark Tank India Young Founders Special">
            {/* The portrait cut, not the wide banner: at 3.5:1 the banner was
                only ~144px tall beside a 500px column and looked stranded. */}
            <img
              src={poster}
              alt="Shark Tank India Young Founders Special — entries are open till 30th October for children under 15."
              width="1400"
              height="1980"
              loading="lazy"
            />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
